'use client';

import { useCallback, useMemo } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { TableOfContentData, TableOfContentDataItem } from '@tiptap/extension-table-of-contents';

import { useToc } from 'containers/Tiptap/components/tiptap-node/toc-node/context/toc-context';

import './toc-node.scss';

type TocTreeNode = {
  item: TableOfContentDataItem;
  depth: number;
};

export function TocNodeComponent(props: NodeViewProps): JSX.Element {
  const { node, extension } = props;
  const { tocContent, navigateToHeading, normalizeHeadingDepths } = useToc();

  const {
    topOffset: optionTopOffset = 0,
    maxShowCount: optionMaxShowCount = 20,
    showTitle: optionShowTitle = true,
  } = extension.options;

  const attrs = node.attrs ?? {};
  const { backgroundColor } = attrs;
  const topOffset = attrs.topOffset ?? optionTopOffset;
  const maxShowCount = attrs.maxShowCount ?? optionMaxShowCount;
  const showTitle = attrs.showTitle ?? optionShowTitle;

  const headingList = useMemo<TableOfContentData>(() => tocContent ?? [], [tocContent]);

  const visibleHeadings = useMemo(() => headingList.slice(0, maxShowCount), [headingList, maxShowCount]);

  const normalizedDepths = useMemo<number[]>(
    () => normalizeHeadingDepths(visibleHeadings),
    [visibleHeadings, normalizeHeadingDepths]
  );

  const flatTocList = useMemo<TocTreeNode[]>(
    () =>
      visibleHeadings.map((item, index) => ({
        item,
        depth: normalizedDepths[index] ?? 1,
      })),
    [visibleHeadings, normalizedDepths]
  );

  const handleContentClick = useCallback(
    (e: React.MouseEvent, item: TableOfContentDataItem) => {
      e.preventDefault();
      e.stopPropagation();
      navigateToHeading(item, { topOffset });
    },
    [navigateToHeading, topOffset]
  );

  const { style: _, ...HTMLAttributes } = props.HTMLAttributes;

  const wrapperProps = {
    className: 'tiptap-table-of-contents-node',
    style: { backgroundColor: backgroundColor || undefined },
    ...HTMLAttributes,
  };

  if (!visibleHeadings.length) {
    return (
      <NodeViewWrapper {...wrapperProps}>
        <div className="tiptap-table-of-contents-empty">Add headings to create a table of contents.</div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper {...wrapperProps}>
      {showTitle && <div className="tiptap-table-of-contents-title">Table of contents</div>}
      <nav aria-label="Table of contents" className="tiptap-table-of-contents-list">
        {flatTocList.map((tocNode, index) => (
          <a
            key={tocNode.item.id ?? `${index}-${tocNode.item.textContent}`}
            href={`#${tocNode.item.id}`}
            rel="noopener noreferrer"
            className="tiptap-table-of-contents-item notranslate"
            data-depth={tocNode.depth}
            style={{ '--toc-depth': tocNode.depth } as React.CSSProperties}
            onClick={(e) => handleContentClick(e, tocNode.item)}
          >
            {tocNode.item.textContent}
          </a>
        ))}
      </nav>
    </NodeViewWrapper>
  );
}

export default TocNodeComponent;
